import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const EmptyContainer = styled.div`
  text-align: center;
  padding: ${props => props.padding || '60px 20px'};
  background: ${props => props.background || 'transparent'};
  border-radius: 12px;
  max-width: 500px;
  margin: 0 auto;
`;

const IconWrapper = styled.div`
  font-size: ${props => props.iconSize || '80px'};
  color: ${props => props.iconColor || '#e0e0e0'};
  margin-bottom: 24px;
  animation: float 3s ease-in-out infinite;

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
`;

const Title = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 24px;
  line-height: 1.5;
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, rgb(242, 128, 0) 0%, #e57706 100%);
  color: white;
  border: none;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(242, 128, 0, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(242, 128, 0, 0.4);
  }

  i {
    margin-right: 8px;
  }
`;

const EmptyState = ({ 
  type = 'no-data',
  title,
  description,
  actionText,
  actionLink,
  icon,
  iconColor,
  iconSize = '80px',
  padding = '60px 20px',
  background = 'transparent'
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const defaultStates = {
    'no-data': {
      icon: 'fas fa-inbox',
      iconColor: '#e0e0e0',
      title: t('No data available'),
      description: t('There is no content to display at the moment'),
      actionText: null,
      actionLink: null
    },
    'no-ads': {
      icon: 'fas fa-search',
      iconColor: '#ddd',
      title: t('No ads found'),
      description: t('Try adjusting your filters or search terms'),
      actionText: null,
      actionLink: null
    },
    'no-messages': {
      icon: 'fas fa-comments',
      iconColor: '#c8e6c9',
      title: t('No messages yet'),
      description: t('Start a conversation by contacting a seller'),
      actionText: t('Browse ads'),
      actionLink: '/ads'
    },
    'no-favorites': {
      icon: 'fas fa-heart',
      iconColor: '#ffcdd2',
      title: t('No favorites yet'),
      description: t('Save ads you like to find them easily later'),
      actionText: t('Explore ads'),
      actionLink: '/ads'
    },
    'no-notifications': {
      icon: 'fas fa-bell',
      iconColor: '#fff9c4',
      title: t('No notifications'),
      description: t('We\'ll notify you when something happens'),
      actionText: null,
      actionLink: null
    },
    'no-results': {
      icon: 'fas fa-search',
      iconColor: '#e0e0e0',
      title: t('No results found'),
      description: t('Try different keywords or remove some filters'),
      actionText: t('Clear filters'),
      actionLink: null
    },
    'no-following': {
      icon: 'fas fa-users',
      iconColor: '#bbdefb',
      title: t('Not following anyone yet'),
      description: t('Follow users to see their ads in your personalized feed'),
      actionText: t('Find users'),
      actionLink: '/ads'
    },
    'no-offers': {
      icon: 'fas fa-tag',
      iconColor: '#f8bbd0',
      title: t('No offers yet'),
      description: t('Buyers can make offers on your negotiable items'),
      actionText: null,
      actionLink: null
    },
    'error': {
      icon: 'fas fa-exclamation-triangle',
      iconColor: '#ffab91',
      title: t('Something went wrong'),
      description: t('Please try again later or contact support'),
      actionText: t('Retry'),
      actionLink: null
    }
  };

  const state = defaultStates[type] || defaultStates['no-data'];
  
  const finalIcon = icon || state.icon;
  const finalIconColor = iconColor || state.iconColor;
  const finalTitle = title || state.title;
  const finalDescription = description || state.description;
  const finalActionText = actionText || state.actionText;
  const finalActionLink = actionLink || state.actionLink;

  const handleAction = () => {
    if (finalActionLink) {
      navigate(finalActionLink);
    }
  };

  return (
    <EmptyContainer padding={padding} background={background}>
      <IconWrapper iconSize={iconSize} iconColor={finalIconColor}>
        <i className={finalIcon}></i>
      </IconWrapper>
      <Title>{finalTitle}</Title>
      <Description>{finalDescription}</Description>
      {finalActionText && (
        <ActionButton onClick={handleAction}>
          <i className="fas fa-arrow-right"></i>
          {finalActionText}
        </ActionButton>
      )}
    </EmptyContainer>
  );
};

export default EmptyState;
